// ============================================================================
// FSA NOTE MODAL — DISCLOSURE VARIABLE GRID
// ============================================================================
// Renders a disclosure whose spec carries only `column_spec` (no fixed_rows).
// User adds rows; each row's cells follow column_spec data_types. Computed
// cells evaluate per-row (formula references sibling columns in same row).

import React, { useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { evaluateFormula } from './formula';
import { DisclosureGroupHeader } from './DisclosureGroupHeader';
import { AmountCellEditor } from './AmountCellEditor';
import {
  autoRound,
  applyRoundingTolerance,
  factorUnitsLabel,
  DEFAULT_ROUNDING_TOLERANCE,
  ROUNDED_SUFFIX,
  type RoundingFactor,
} from '../../shared/rounding';

export interface ColumnSpec {
  code: string;
  label: string;
  data_type: string;
  is_computed?: boolean;
  formula?: string;
  options?: string[];
  align?: string;
  /** Optional banner label rendered above this column in a spanning header
   *  cell (e.g. "Current Year" / "Previous Year"). Consecutive columns
   *  with the same group value share one banner cell. */
  group?: string;
  /**
   * Optional column width override. Accepts CSS values: "120px", "1fr",
   * "25%" — or plain number (treated as px). When absent the renderer
   * falls back to a sensible default for the data_type (amount → 120px,
   * integer → 100px, percent → 80px, date → 110px, enum → 140px, text →
   * auto-fills remaining space).
   */
  width?: string | number;
  /**
   * When TRUE on a `data_type: 'text'` column, the cell renders as a
   * multi-line `<textarea>` (auto-growing, preserves newlines) instead
   * of a single-line `<input>`. Used by disclosures whose text cells
   * carry bulleted prose — e.g. LT Borrowings "Nature of Security" /
   * "Terms of Repayment" where the auditor writes one item per line.
   * Default FALSE (single-line input).
   */
  multiline?: boolean;
  /**
   * Optional number of textarea rows when `multiline: true`. Defaults
   * to 3. Ignored when not multiline.
   */
  rows?: number;
}

/**
 * Width resolver shared by DisclosureVariableGrid + DisclosureFixedGrid.
 * Per-column override wins; otherwise the data_type maps to a default.
 * The first auto column (typically the text/Particulars column) fills
 * remaining table space — handled by table-layout: fixed + CSS auto.
 */
export function resolveColumnWidth(c: ColumnSpec): string {
  if (c.width != null) {
    return typeof c.width === 'number' ? `${c.width}px` : String(c.width);
  }
  switch (c.data_type) {
    case 'amount':  return '120px';
    case 'integer': return '100px';
    case 'percent': return '80px';
    case 'date':    return '110px';
    case 'enum':    return '140px';
    case 'text':
    default:        return 'auto';
  }
}

type Row = Record<string, string | number | null>;

interface Props {
  columns: ColumnSpec[];
  rows: Row[];
  onChange: (rows: Row[]) => void;
  readOnly: boolean;
  /** Engagement rounding-factor key — drives amount cells' auto-round
   *  companion. NULL falls back to ACTUALS. */
  roundingFactor?: RoundingFactor | null;
  /** Engagement rounding tolerance — preserves user override on amount
   *  cells when within tolerance. */
  roundingTolerance?: number;
  /**
   * Note-Modal-level toggle. Controls whether the amount cell shows
   * its actual input, its rounded input, or both. Defaults to `'both'`
   * for backwards compatibility.
   */
  displayMode?: 'actual' | 'rounded' | 'both';
}

const DisclosureVariableGrid: React.FC<Props> = ({
  columns,
  rows,
  onChange,
  readOnly,
  roundingFactor,
  roundingTolerance,
  displayMode = 'both',
}) => {
  const factor = roundingFactor ?? 'ACTUALS';
  const tolerance = roundingTolerance ?? DEFAULT_ROUNDING_TOLERANCE;
  const factorUnits = factorUnitsLabel(factor);

  const handleCellChange = useCallback(
    (rowIdx: number, col: ColumnSpec, value: string | number | null) => {
      const next = rows.map((r, i) => (i === rowIdx ? { ...r, [col.code]: value } : r));
      onChange(recomputeRows(next, columns, factor, tolerance));
    },
    [rows, columns, onChange, factor, tolerance],
  );

  // Amount cells write BOTH the actual and rounded companion in one patch.
  const handleAmountCellChange = useCallback(
    (rowIdx: number, col: ColumnSpec, actual: number, rounded: number) => {
      const next = rows.map((r, i) =>
        i === rowIdx
          ? { ...r, [col.code]: actual, [col.code + ROUNDED_SUFFIX]: rounded }
          : r,
      );
      onChange(recomputeRows(next, columns, factor, tolerance));
    },
    [rows, columns, onChange, factor, tolerance],
  );

  const handleAddRow = useCallback(() => {
    const empty = blankRow(columns, factor);
    onChange(recomputeRows([...rows, empty], columns, factor, tolerance));
  }, [rows, columns, onChange, factor, tolerance]);

  const handleDeleteRow = useCallback(
    (rowIdx: number) => {
      onChange(rows.filter((_, i) => i !== rowIdx));
    },
    [rows, onChange],
  );

  return (
    <div>
      <table style={gridTableStyle}>
        <colgroup>
          {columns.map(c => (
            <col key={c.code} style={{ width: resolveColumnWidth(c), minWidth: 80 }} />
          ))}
          {!readOnly && <col style={{ width: 40, minWidth: 40 }} />}
        </colgroup>
        <thead>
          <DisclosureGroupHeader
            columns={columns}
            trailingCellWidth={!readOnly ? 40 : undefined}
            cellStyle={thStyle}
            groupCellStyle={() => thGroupStyle}
            renderLabel={c => (
              <>
                {c.label}
                {c.is_computed && (
                  <span style={computedChipStyle} title={`Computed: ${c.formula}`}> ƒ</span>
                )}
              </>
            )}
          />
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length + (readOnly ? 0 : 1)} style={emptyRowStyle}>
                No rows yet. {!readOnly && 'Click + Add row.'}
              </td>
            </tr>
          ) : (
            rows.map((row, i) => (
              <tr key={i}>
                {columns.map(c => {
                  // Amount columns always use the dual-storage editor.
                  // Computed amount columns (e.g. Authorised Capital's
                  // amount_cy = shares × face_value) lock the actual
                  // input — the formula owns it — but keep the rounded
                  // input EDITABLE so the auditor can hold an override
                  // on the auto-summed rounded total.
                  if (c.data_type === 'amount') {
                    const actual = typeof row[c.code] === 'number' ? (row[c.code] as number) : 0;
                    const rounded = typeof row[c.code + ROUNDED_SUFFIX] === 'number'
                      ? (row[c.code + ROUNDED_SUFFIX] as number)
                      : autoRound(actual, factor);
                    return (
                      <td
                        key={c.code}
                        style={tdStyle(c)}
                        title={c.is_computed ? `Computed: ${c.formula}` : undefined}
                      >
                        <AmountCellEditor
                          actualValue={actual}
                          roundedValue={rounded}
                          factor={factor}
                          tolerance={tolerance}
                          isComputed={c.is_computed === true}
                          readOnly={readOnly}
                          onChange={(a, r) => handleAmountCellChange(i, c, a, r)}
                          factorHint={factorUnits}
                          displayMode={displayMode}
                        />
                      </td>
                    );
                  }
                  return (
                    <td key={c.code} style={tdStyle(c)}>
                      <CellRenderer
                        spec={c}
                        value={row[c.code]}
                        onChange={v => handleCellChange(i, c, v)}
                        readOnly={readOnly}
                      />
                    </td>
                  );
                })}
                {!readOnly && (
                  <td style={{ ...tdBaseStyle, textAlign: 'center', width: 40 }}>
                    <button
                      type="button"
                      className="btn btn-icon btn-sm"
                      title="Delete row"
                      onClick={() => handleDeleteRow(i)}
                      style={inlineIconBtnStyle}
                    >
                      <IconSystem.trash size={12} />
                    </button>
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
      {!readOnly && (
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={handleAddRow}
          style={{ marginTop: 8 }}
        >
          <IconSystem.add size={13} /> Add row
        </button>
      )}
    </div>
  );
};

// ───── helpers ──────────────────────────────────────────────────────────────

function blankRow(columns: ColumnSpec[], factor: RoundingFactor = 'ACTUALS'): Row {
  const r: Row = {};
  for (const c of columns) {
    if (c.data_type === 'amount') {
      r[c.code] = 0;
      r[c.code + ROUNDED_SUFFIX] = 0;
    } else if (c.data_type === 'integer' || c.data_type === 'percent') {
      r[c.code] = 0;
    } else {
      r[c.code] = '';
    }
  }
  return r;
}

/** Recompute all derived cells per-row so the saved JSON carries every
 *  value the display + PDF will read. Two passes:
 *    1. Per-column formulas evaluate against canonical actuals. For
 *       amount columns, the SAME formula evaluates again against the
 *       sibling-column `__rounded` companions so the rounded total
 *       equals the sum of displayed rounded values (Schedule III audit
 *       convention) and not autoRound-of-actual-sum.
 *    2. Editable amount columns reconcile `__rounded` from their actual
 *       via autoRound + tolerance preserve.
 *
 *  Auditor override on a computed amount's `__rounded` is honoured
 *  within tolerance — matches the audit override pattern. */
function recomputeRows(
  rows: Row[],
  columns: ColumnSpec[],
  factor: RoundingFactor = 'ACTUALS',
  tolerance: number = DEFAULT_ROUNDING_TOLERANCE,
): Row[] {
  const columnsByCode = new Map<string, ColumnSpec>();
  for (const c of columns) columnsByCode.set(c.code, c);

  return rows.map(row => {
    const next: Row = { ...row };

    // Seed missing __rounded companions from autoRound so the rounded
    // formula eval below has a number to read on legacy single-storage
    // rows. The seed is persisted on this save — first edit migrates.
    for (const c of columns) {
      if (c.data_type !== 'amount') continue;
      if (typeof next[c.code + ROUNDED_SUFFIX] === 'number') continue;
      const a = typeof next[c.code] === 'number' ? (next[c.code] as number) : 0;
      next[c.code + ROUNDED_SUFFIX] = autoRound(a, factor);
    }

    // Pass 1 — computed columns. Actual evaluates against canonical
    // names; rounded evaluates against the same formula with name lookups
    // redirected to __rounded for amount-typed siblings.
    for (const c of columns) {
      if (!c.is_computed || !c.formula) continue;

      // Actual.
      next[c.code] = evaluateFormula(c.formula, name => {
        const v = next[name];
        return typeof v === 'number' ? v : v != null && v !== '' ? Number(v) || 0 : 0;
      });

      if (c.data_type === 'amount') {
        const newRounded = evaluateFormula(c.formula, name => {
          const ref = columnsByCode.get(name);
          // Amount siblings → use rounded companion. Integer / percent
          // siblings (e.g. shares × face_value_rounded) read as-is.
          const key = ref?.data_type === 'amount' ? name + ROUNDED_SUFFIX : name;
          const v = next[key];
          return typeof v === 'number' ? v : v != null && v !== '' ? Number(v) || 0 : 0;
        });
        const stored = typeof next[c.code + ROUNDED_SUFFIX] === 'number'
          ? (next[c.code + ROUNDED_SUFFIX] as number)
          : newRounded;
        next[c.code + ROUNDED_SUFFIX] = applyRoundingTolerance(stored, newRounded, tolerance);
      }
    }

    // Pass 2 — editable amount columns: reconcile __rounded from actual.
    for (const c of columns) {
      if (c.data_type !== 'amount' || c.is_computed) continue;
      const actual = typeof next[c.code] === 'number' ? (next[c.code] as number) : 0;
      const auto = autoRound(actual, factor);
      const stored = typeof next[c.code + ROUNDED_SUFFIX] === 'number'
        ? (next[c.code + ROUNDED_SUFFIX] as number)
        : auto;
      next[c.code + ROUNDED_SUFFIX] = applyRoundingTolerance(stored, auto, tolerance);
    }

    return next;
  });
}

// ───── cell renderer (dispatch by data_type) ────────────────────────────────

const CellRenderer: React.FC<{
  spec: ColumnSpec;
  value: string | number | null | undefined;
  onChange: (v: string | number | null) => void;
  readOnly: boolean;
}> = ({ spec, value, onChange, readOnly }) => {
  if (spec.is_computed) {
    const numeric = typeof value === 'number' ? value : Number(value) || 0;
    return (
      <span style={computedCellStyle}>
        {formatByType(spec.data_type, numeric)}
      </span>
    );
  }

  const isDisabled = readOnly;

  switch (spec.data_type) {
    case 'amount':
    case 'integer':
    case 'percent': {
      const numeric = typeof value === 'number' ? value : Number(value) || 0;
      const input = (
        <input
          type="number"
          className="form-input"
          value={numeric}
          step={spec.data_type === 'integer' ? '1' : '0.01'}
          readOnly={isDisabled}
          disabled={isDisabled}
          onChange={e => onChange(Number(e.target.value))}
          style={spec.data_type === 'percent' ? numericInputPercentStyle : numericInputStyle}
        />
      );
      // Percent cells get a trailing `%` suffix overlay so the auditor
      // sees the unit during entry. Stored value is the bare number.
      if (spec.data_type === 'percent') {
        return (
          <span style={percentWrapStyle}>
            {input}
            <span style={percentSuffixStyle}>%</span>
          </span>
        );
      }
      return input;
    }
    case 'enum':
      return (
        <select
          className="form-input"
          value={(value ?? '') as string}
          disabled={isDisabled}
          onChange={e => onChange(e.target.value)}
          style={enumInputStyle}
        >
          <option value="">—</option>
          {(spec.options ?? []).map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    case 'date':
      return (
        <input
          type="date"
          className="form-input"
          value={(value ?? '') as string}
          readOnly={isDisabled}
          disabled={isDisabled}
          onChange={e => onChange(e.target.value)}
          style={enumInputStyle}
        />
      );
    case 'text':
    default: {
      if (spec.multiline) {
        return (
          <textarea
            className="form-input"
            value={(value ?? '') as string}
            rows={typeof spec.rows === 'number' && spec.rows > 0 ? spec.rows : 3}
            readOnly={isDisabled}
            disabled={isDisabled}
            onChange={e => onChange(e.target.value)}
            style={multilineTextInputStyle}
          />
        );
      }
      return (
        <input
          type="text"
          className="form-input"
          value={(value ?? '') as string}
          readOnly={isDisabled}
          disabled={isDisabled}
          onChange={e => onChange(e.target.value)}
          style={textInputStyle}
        />
      );
    }
  }
};

function formatByType(dataType: string, v: number): string {
  if (dataType === 'amount') {
    return v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  if (dataType === 'integer') {
    return v.toLocaleString('en-IN');
  }
  if (dataType === 'percent') {
    return `${v.toFixed(2)}%`;
  }
  return String(v);
}

// ───── styles ────────────────────────────────────────────────────────────────

const gridTableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.78rem',
  // table-layout: fixed honours the colgroup widths; long cell content
  // wraps inside the cell instead of forcing the column wider.
  tableLayout: 'fixed',
  wordBreak: 'break-word',
};
const thBaseStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '6px 10px',
  background: 'var(--bg-secondary)',
  borderBottom: '1px solid var(--border-color)',
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: 0.4,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
};
const tdBaseStyle: React.CSSProperties = {
  padding: '4px 8px',
  borderBottom: '1px solid var(--border-color)',
  verticalAlign: 'middle',
};
const thStyle = (c: ColumnSpec): React.CSSProperties => ({
  ...thBaseStyle,
  textAlign: c.align === 'right' ? 'right' : 'left',
});
// Style for the spanning "Current Year" / "Previous Year" group header
// cells. Centered, slightly muted background so the group label stays
// visually distinct from the metric-column labels underneath.
const thGroupStyle: React.CSSProperties = {
  ...thBaseStyle,
  textAlign: 'center',
  background: 'var(--bg-tertiary, #e2e8f0)',
};
const tdStyle = (c: ColumnSpec): React.CSSProperties => ({
  ...tdBaseStyle,
  textAlign: c.align === 'right' ? 'right' : 'left',
});
const computedChipStyle: React.CSSProperties = {
  marginLeft: 4,
  color: '#d97706',
  fontWeight: 700,
  cursor: 'help',
};
const computedCellStyle: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
};
const numericInputStyle: React.CSSProperties = {
  width: '100%',
  fontSize: '0.78rem',
  padding: '2px 6px',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};
// Percent inputs leave a hair of space on the right for the `%` suffix
// rendered by the wrapping span. Keeps the suffix visually attached to
// the typed number without cramping it.
const numericInputPercentStyle: React.CSSProperties = {
  ...{
    width: '100%',
    fontSize: '0.78rem',
    padding: '2px 16px 2px 6px',
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  },
};
const percentWrapStyle: React.CSSProperties = {
  position: 'relative',
  display: 'inline-block',
  width: '100%',
};
const percentSuffixStyle: React.CSSProperties = {
  position: 'absolute',
  right: 6,
  top: '50%',
  transform: 'translateY(-50%)',
  fontSize: '0.7rem',
  color: 'var(--text-secondary)',
  pointerEvents: 'none',
};
const textInputStyle: React.CSSProperties = {
  width: '100%',
  fontSize: '0.78rem',
  padding: '2px 6px',
};
const multilineTextInputStyle: React.CSSProperties = {
  width: '100%',
  fontSize: '0.78rem',
  padding: '4px 6px',
  resize: 'vertical',
  fontFamily: 'inherit',
  lineHeight: 1.4,
  whiteSpace: 'pre-wrap',
};
const enumInputStyle: React.CSSProperties = {
  width: '100%',
  fontSize: '0.78rem',
  padding: '2px 6px',
};
const emptyRowStyle: React.CSSProperties = {
  padding: '16px',
  textAlign: 'center',
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
  fontSize: '0.78rem',
};
const inlineIconBtnStyle: React.CSSProperties = {
  padding: '2px 4px',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: '#dc2626',
};

export default DisclosureVariableGrid;
